import { createEntityAdapter, createSlice } from '@reduxjs/toolkit';

import {
    createMeasurement,
    editMeasurement,
    fetchMeasurements,
    getMeasurement,
    loadMoreMeasurements,
    removeMeasurement,
} from './action';
import { mainCalculations } from '../../../TemporaryConmonents/Measurements/helpers/mainCalculations';
import { recalculations } from '../../../TemporaryConmonents/Measurements/helpers/recalculations';

const measurementsSAdapter = createEntityAdapter();

const initialState = measurementsSAdapter.getInitialState({
    measurementsLoading: false,
    modalLoading: false,
    measurementsLoadingMore: false,
    measurementsNeedFetch: true,
    needFetchMore: false,
    error: null,
    isShowModal: false,
    isManualCalculations: false,
    currentCardId: '',
    isOpenPrevImage: false,
    isEdited: false,
    openingsList: {
        ids: [],
        entities: {},
    },
    additionallyList: {
        ids: [],
        entities: {},
    },
    attachments: { ids: [], entities: {} },
    calculations: {
        id: '',
        name: '',
        in_edit_mode: false,
        length: '',
        width: '',
        height: '',
        floor_area: '',
        ceiling_area: '',
        wall_area: '',
        perimeter: '',
        without_opening: '',
        volume: '',
        door_quantity: '',
        door_area: '',
        door_perimeter: '',
        door_slope_area: '',
        door_slope_perimeter: '',
        window_quantity: '',
        window_area: '',
        window_perimeter: '',
        window_slope_area: '',
        window_slope_perimeter: '',
        comment: '',
    },
    manualCalculations: {
        length: '',
        width: '',
        height: '',
        floor_area: '',
        ceiling_area: '',
        wall_area: '',
        perimeter: '',
        without_opening: '',
        volume: '',
        door_quantity: '',
        door_area: '',
        door_perimeter: '',
        door_slope_area: '',
        door_slope_perimeter: '',
        window_quantity: '',
        window_area: '',
        window_perimeter: '',
        window_slope_area: '',
        window_slope_perimeter: '',
    },
});

const measurementsSlice = createSlice({
    name: 'measurements',
    initialState,
    reducers: {
        addMeasurements: measurementsSAdapter.addMany,
        updateOne: measurementsSAdapter.updateOne,
        updateMany: measurementsSAdapter.updateMany,
        changeNeedMore: (state, { payload }) => {
            state.needFetchMore = payload;
        },
        changeNeedMeasurements: (state, { payload }) => {
            state.measurementsNeedFetch = payload;
        },
        changeIsShowModal: (state, { payload }) => {
            state.isShowModal = payload;
            if (!payload) {
                state.isEdited = false;
                state.isManualCalculations = false;
                state.currentCardId = '';
                state.openingsList = {
                    ids: [],
                    entities: {},
                };
                state.additionallyList = {
                    ids: [],
                    entities: {},
                };
                state.attachments = { ids: [], entities: {} };
                state.calculations = {
                    id: '',
                    name: '',
                    in_edit_mode: false,
                    length: '',
                    width: '',
                    height: '',
                    floor_area: '',
                    ceiling_area: '',
                    wall_area: '',
                    perimeter: '',
                    without_opening: '',
                    volume: '',
                    door_quantity: '',
                    door_area: '',
                    door_perimeter: '',
                    door_slope_area: '',
                    door_slope_perimeter: '',
                    window_quantity: '',
                    window_area: '',
                    window_perimeter: '',
                    window_slope_area: '',
                    window_slope_perimeter: '',
                    comment: '',
                };
                state.manualCalculations = {
                    length: '',
                    width: '',
                    height: '',
                    floor_area: '',
                    ceiling_area: '',
                    wall_area: '',
                    perimeter: '',
                    without_opening: '',
                    volume: '',
                    door_quantity: '',
                    door_area: '',
                    door_perimeter: '',
                    door_slope_area: '',
                    door_slope_perimeter: '',
                    window_quantity: '',
                    window_area: '',
                    window_perimeter: '',
                    window_slope_area: '',
                    window_slope_perimeter: '',
                };
            }
        },
        changeCurrentCardId: (state, { payload }) => {
            state.currentCardId = payload.toString();
            state.isShowModal = true;
        },
        addAttachments: (state, { payload }) => {
            if (!state.isEdited) {
                state.isEdited = true;
            }

            const newIds = [];
            const newEntities = {};

            payload.forEach((attachment) => {
                const { id } = attachment;
                newIds.push(id);
                newEntities[id] = attachment;
            });

            state.attachments.ids = newIds;
            state.attachments.entities = newEntities;
        },
        removeAttachments: (state, { payload }) => {
            if (!state.isEdited) {
                state.isEdited = true;
            }
            const id = payload;
            state.attachments.ids = state.attachments.ids.filter(
                (attachmentId) => attachmentId !== id
            );
            delete state.attachments.entities[id];
        },
        addOpening: (state, { payload }) => {
            if (!state.isEdited) {
                state.isEdited = true;
            }
            const { id } = payload;
            if (!state.openingsList.ids.includes(id)) {
                state.openingsList.ids.push(id);
                state.openingsList.entities[id] = payload;

                const updatedList = state.openingsList.entities;
                updatedList[id] = payload;

                state.calculations = recalculations(updatedList, state.calculations);

                if (!state.isManualCalculations) {
                    state.manualCalculations = state.calculations;
                }
            }
        },
        addManyOpenings: (state, { payload }) => {
            payload.forEach((openin) => {
                const { id } = openin;
                if (!state.openingsList.ids.includes(id)) {
                    state.openingsList.ids.push(id);
                    state.openingsList.entities[id] = openin;
                }
            });
        },
        resetOpening: (state) => {
            state.openingsList = {
                ids: [],
                entities: {},
            };
        },
        removeOpening: (state, { payload }) => {
            if (!state.isEdited) {
                state.isEdited = true;
            }
            const id = payload;
            state.openingsList.ids = state.openingsList.ids.filter((openingId) => openingId !== id);
            delete state.openingsList.entities[id];

            const updatedList = state.openingsList.entities;
            delete updatedList[id];

            state.calculations = recalculations(updatedList, state.calculations);

            if (!state.isManualCalculations) {
                state.manualCalculations = state.calculations;
            }
        },
        changeOpening: (state, { payload }) => {
            if (!state.isEdited) {
                state.isEdited = true;
            }
            const { id, changes } = payload;
            const opening = state.openingsList.entities[id];
            if (opening) {
                state.openingsList.entities[id] = {
                    ...opening,
                    ...changes,
                };

                const updatedList = state.openingsList.entities;
                updatedList[id] = {
                    ...opening,
                    ...changes,
                };

                state.calculations = recalculations(updatedList, state.calculations);

                if (!state.isManualCalculations) {
                    state.manualCalculations = state.calculations;
                }
            }
        },
        addManyAdditionally: (state, { payload }) => {
            state.additionallyList = {
                ids: [],
                entities: {},
            };
            payload.forEach((additionally) => {
                state.additionallyList.ids.push(additionally.id);
                state.additionallyList.entities[additionally.id] = additionally;
            });
        },
        addAdditionally: (state, { payload }) => {
            if (!state.isEdited) {
                state.isEdited = true;
            }
            const { id } = payload;
            if (!state.additionallyList.ids.includes(id)) {
                state.additionallyList.ids.push(id);
                state.additionallyList.entities[id] = payload;
            }
        },
        removeAdditionally: (state, { payload }) => {
            if (!state.isEdited) {
                state.isEdited = true;
            }
            const id = payload;
            state.additionallyList.ids = state.additionallyList.ids.filter(
                (openingId) => openingId !== id
            );
            delete state.additionallyList.entities[id];
        },
        changeAdditionally: (state, { payload }) => {
            if (!state.isEdited) {
                state.isEdited = true;
            }
            const { id, changes } = payload;
            const opening = state.additionallyList.entities[id];
            if (opening) {
                state.additionallyList.entities[id] = {
                    ...opening,
                    ...changes,
                };
            }
        },
        changeIsManualCalculations: (state) => {
            if (!state.isEdited) {
                state.isEdited = true;
            }
            state.calculations.in_edit_mode = !state.isManualCalculations;
            state.isManualCalculations = !state.isManualCalculations;

            if (!state.isManualCalculations) {
                state.manualCalculations = recalculations(
                    state.openingsList.entities,
                    state.calculations
                );
                state.calculations = recalculations(
                    state.openingsList.entities,
                    state.calculations
                );
            }
        },
        setIsManualCalculations: (state, { payload }) => {
            if (!state.isEdited) {
                state.isEdited = true;
            }
            state.calculations.in_edit_mode = payload;
            state.isManualCalculations = payload;

            if (!state.isManualCalculations) {
                state.manualCalculations = state.calculations;
            }
        },
        changeCalculations: (state, { payload }) => {
            if (!state.isEdited) {
                state.isEdited = true;
            }
            const updatedCalculations = {
                ...state.calculations,
                ...payload,
            };
            state.calculations = mainCalculations(updatedCalculations);

            if (!state.isManualCalculations) {
                state.manualCalculations = state.calculations;
            }
        },
        changeManualCalculations: (state, { payload }) => {
            if (!state.isEdited) {
                state.isEdited = true;
            }
            state.manualCalculations = {
                ...state.manualCalculations,
                ...payload,
            };
        },
        changeIsOpenPrevImage: (state, { payload }) => {
            state.isOpenPrevImage = payload;
        },
        changeIsEdited: (state, { payload }) => {
            state.isEdited = payload;
        },
        addGeneratedMeasurements: (state, { payload }) => {
            measurementsSAdapter.addMany(state, payload);

            const newIds = payload.map((item) => item.id);
            state.ids = [...newIds, ...state.ids.filter((id) => !newIds.includes(id))];
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchMeasurements.pending, (state) => {
                state.measurementsLoading = true;
                state.error = null;
            })
            .addCase(fetchMeasurements.fulfilled, (state, action) => {
                state.measurementsLoading = false;
                measurementsSAdapter.addMany(state, action.payload);
            })
            .addCase(fetchMeasurements.rejected, (state, action) => {
                state.measurementsLoading = false;
                state.error = action.error.message;
            })
            .addCase(loadMoreMeasurements.pending, (state) => {
                state.measurementsLoadingMore = true;
                state.error = null;
            })
            .addCase(loadMoreMeasurements.fulfilled, (state, action) => {
                state.measurementsLoadingMore = false;
                measurementsSAdapter.addMany(state, action.payload);
            })
            .addCase(loadMoreMeasurements.rejected, (state, action) => {
                state.measurementsLoadingMore = false;
                state.error = action.error.message;
            })
            .addCase(getMeasurement.pending, (state) => {
                state.modalLoading = true;
                state.error = null;
            })
            .addCase(getMeasurement.fulfilled, (state, action) => {
                state.modalLoading = false;
            })
            .addCase(getMeasurement.rejected, (state, action) => {
                state.modalLoading = false;
                state.error = action.error.message;
            })
            .addCase(createMeasurement.pending, (state) => {
                state.modalLoading = true;
                state.error = null;
            })
            .addCase(createMeasurement.fulfilled, (state, action) => {
                state.modalLoading = false;
                measurementsSAdapter.addOne(state, action.payload);

                const newId = action.payload.id;
                state.ids = [newId, ...state.ids.filter((id) => newId !== id)];
                state.modalLoading = false;
            })
            .addCase(createMeasurement.rejected, (state, action) => {
                state.modalLoading = false;
                state.error = action.error.message;
            })
            .addCase(removeMeasurement.pending, (state) => {
                state.modalLoading = true;
                state.error = null;
            })
            .addCase(removeMeasurement.fulfilled, (state, { payload }) => {
                measurementsSAdapter.removeOne(state, payload);
                state.modalLoading = false;
            })
            .addCase(removeMeasurement.rejected, (state, action) => {
                state.modalLoading = false;
                state.error = action.error.message;
            })
            .addCase(editMeasurement.pending, (state) => {
                state.modalLoading = true;
                state.error = null;
            })
            .addCase(editMeasurement.fulfilled, (state, { payload }) => {
                measurementsSAdapter.updateOne(state, payload);
                state.modalLoading = false;
            })
            .addCase(editMeasurement.rejected, (state, action) => {
                state.modalLoading = false;
                state.error = action.error.message;
            });
    },
});

export const measurementsSelector = measurementsSAdapter.getSelectors(
    (state) => state.measurementsReducer
);
export const { actions: measurementsActions } = measurementsSlice;
export const { reducer: measurementsReducer } = measurementsSlice;
